<template>
  <section class="batch-details section">
    <div class="columns is-centered no-print">
      <div class="column is-full has-text-centered">
        <div class="block">
          <button class="button is-primary" @click="print()"><i class="fa fa-print"></i>&nbsp;Print</button>
        </div>
        <div class="block">
          <b-checkbox v-model="print_options.enable_directions" true-value="1" false-value="0" @input="updatePrintOptions()">
            Directions
          </b-checkbox>
          <b-checkbox  v-show="shouldShowActualTravelCheckbox" v-model="print_options.enable_actual_directions" true-value="1" false-value="0" @input="updatePrintOptions()">
            Show Actual Travel Directions
          </b-checkbox>
          <b-checkbox v-model="print_options.enable_start_at" true-value="1" false-value="0" @input="updatePrintOptions()">
            Start at
          </b-checkbox>
          <b-checkbox v-model="print_options.enable_dates" true-value="1" false-value="0" @input="updatePrintOptions()">
            Pickup & arrival dates
          </b-checkbox>
          <b-checkbox v-model="print_options.enable_items" true-value="1" false-value="0" @input="updatePrintOptions()">
            Items
          </b-checkbox>
          <b-checkbox v-model="print_options.enable_stop_actions" true-value="1" false-value="0" @input="updatePrintOptions()">
            Stop Actions
          </b-checkbox>
          <b-checkbox v-if="is_batch" v-model="print_options.enable_map_each_job" true-value="1" false-value="0" @input="updatePrintOptions()">
            Map on each Job
          </b-checkbox>
          <b-checkbox v-model="print_options.enable_page_breaks" true-value="1" false-value="0" @input="updatePrintOptions()">
            Page break between jobs
          </b-checkbox>
          <b-checkbox v-model="print_options.enable_page_header" true-value="1" false-value="0" @input="updatePrintOptions()">
            Header above each job
          </b-checkbox>
          <b-checkbox v-model="print_options.enable_breaks" true-value="1" false-value="0" @input="updatePrintOptions('breaks')">
            Breaks
          </b-checkbox>
        </div>
        <hr />
      </div>
    </div>
    <div class="columns is-centered no-print" v-if="error_message">
      <div class="column is-half">
        <b-message type="is-danger">
          {{ error_message }}
        </b-message>
      </div>
    </div>
    <!-- Start Header Table -->
    <section>
      <table class="table" width="100%">
        <tbody>
        <ManifestHeader :driver="driver" :vehicle="vehicle" :company="company" :vehicleName="getVehicleDisplayName()"></ManifestHeader>
        </tbody>
      </table>
    </section>
    <hr />
    <!-- End Header Table -->

    <!-- Start Map Table -->
    <table class="table" width="100%">
      <tbody>
      <tr>
        <td style="padding-right: 10px; width: 35%">
          <div>
            <strong>Total Route:</strong> {{ this.getTotalDuration() }} min -&nbsp;{{ this.getTotalDistance() }} miles
            <br />
            <strong>Start Date:</strong> {{ this.getBatchStartDate() | formatDate }}
          </div>
          <br />
          <table class="table">
            <tr>
              <td>
                <ol>
                  <li v-for="stop of this.route_data.markers" v-bind:key="stop.job_stop_id">
                    <span>{{ stop.location.long_name }}</span>
                    <br />
                    <strong>{{ getJobContactName(stop.job_id, stop.job_stop_id) }}</strong>
                    <span class="has-text-grey is-size-6">({{ stop.type | capitalize }})</span>
                  </li>
                </ol>
              </td>
            </tr>
          </table>
        </td>
        <td style="width: 50%">
          <div class="map">
            <div v-if="markers">
                <ManifestMap
                  v-if="markers.length !== 0"
                  class="widget-map"
                  :markers="markers"
                  :pathSuggested="path_suggested_travelled"
                  :pathTravelled="path_actual_travelled"
                  :polySuggestedOption="{strokeOpacity: 0.4}"
                  :polyTravelledOption="{strokeColor: '#209cee'}"
                  style="width: 430px; height: 323px"/>
            </div>
          </div>
          <table class="table" width="100%">
            <tr>
              <td align="center">
                <div class="field is-grouped">
                  <div class="control">
                    <div class="tags has-addons">
                      <span class="tag">Suggested Route : &nbsp;<b class="route" style="color: gray;">______</b></span>
                    </div>
                  </div>
                  <div class="control">
                    <div class="tags has-addons">
                      <span class="tag"> Travelled Route : &nbsp;<b class="route" style="color: #3393f1;">______</b></span>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </table>
        </td>
      </tr>
      </tbody>
    </table>
    <!-- End Map Table -->
    <hr />
    <!-- Start Job Table -->
    <table style="width: 100%" v-for="(leg, index) in this.legs" :key="leg" v-bind:class="{ 'page-break': shouldIncludePageBreaks() }">
      <tbody>

      <!-- Start Header Table -->
      <ManifestHeader v-if="shouldShowPageHeader()" :driver="driver" :vehicle="vehicle" :company="company" :vehicleName="getVehicleDisplayName()"></ManifestHeader>
      <tr v-if="shouldShowPageHeader()">
        <td colspan="2"><hr /></td>
      </tr>
      <!-- End Header Table -->
      <tr>
        <td width="50%">
          <p v-if="showJobHeading(leg, index)">
            <strong>Job {{ leg.end_job.job_number }}:</strong>
            <br />{{ leg.end_job.contact_name }}
            <br/>{{ leg.end_stop.contact_phone_national || leg.end_job.phone_national }}
            <span v-if="leg.end_job.driver_notes">
                <br />
                {{ leg.end_job.driver_notes }}
                <br />
              </span>
          </p>
          <p v-if="leg.end_stop.notes">
            <br />
            <strong>Notes:</strong> {{leg.end_stop.notes }}
            <br /><br />
          </p>
          <br />
          <div v-if="showJobHeading(leg, index) && print_options.enable_breaks === '1' && breaks[leg.end_job.job_id].length !== 0">
            <strong>Breaks:</strong>
            <div v-for="(element, break_key) in breaks[leg.end_job.job_id]" :key='break_key'>
              <p>Started : {{element.date_created | formatDate}}</p>
              <p>Status : {{element.status.display}}</p>
              <p>Completed : {{element.completed_date | formatDate}}</p>
              <p>Type: {{element.type.display}}</p>
              <br />
            </div>
            <br />
            <br />
          </div>
          <table v-if="print_options.enable_start_at === '1'">
            <tr>
              <td>
                <span class="has-text-grey is-size-6">Start at:</span>
                <br />{{ leg.start_stop.location.long_name }}
                <span v-if="print_options.enable_dates == 1">
                    <br />
                  <!--
                    If this is the first leg, actually use the start_stop start date
                    else use the start stop completed date b/c the driver will start this new leg
                    when they complete the previous stop
                  -->
                    <span v-if="index == 0" v-html="getStopStartDate(leg.start_stop)"></span>
                    <span v-if="index > 0" v-html="getStopCompletedDate(leg.start_stop)"></span>
                  </span>
                <br />
                <br />
              </td>
            </tr>
            <tr v-if="print_options.enable_directions == 1" v-for="step in getSteps(leg)" :key="step">
              <td>
                <table style="width: 100%">
                  <tr>
                    <td v-if="step.maneuver" width="7%" valign="middle">
                      <div v-bind:class="step.maneuver" class="maneuver"></div>
                    </td>
                    <td v-if="!step.maneuver" width="7%" valign="middle">
                      &nbsp;
                    </td>
                    <td width="93%">
                      <div v-html="step.html_instructions"></div>
                      <span class="has-text-grey is-size-7"><em>{{ step.distance.text }} - {{ step.duration.text }}</em></span>
                    </td>
                  </tr>
                </table>
                <br />
              </td>
            </tr>
            <span v-if="leg.start_stop.driver_notes"><strong>Driver note: </strong> {{leg.start_stop.driver_notes}}</span>
            <br />
            <br />
          </table>
        </td>
        <td>
          <div v-if="print_options.enable_map_each_job === '1'" style = "position:relative; left: 40px;">
          <ManifestMap
            v-if="is_batch && print_options.enable_map_each_job === '1'"
            class="widget-map"
            :markers="markersJobPair[leg.end_job.job_id]"
            :pathSuggested="null"
            :pathTravelled="[route_data.jobs_actual_traveled_polylines[leg.end_job.job_id]]"
            :polySuggestedOption="{strokeOpacity: 0.4}"
            :polyTravelledOption="{strokeColor: '#209cee'}"
            style="width: 430px; height: 323px"/>
            <br />
          </div>
          <div width="50%" v-if="showJobHeading(leg, index) && leg.end_job.items && print_options.enable_items == 1" style="padding-left: 20px">
          <p><strong>{{ leg.end_job.items.length }} Items:</strong></p>
          <table style="width: 100%; margin-bottom: 20px;" v-for="item of leg.end_job.items" :key="item" align="right">
            <tbody>
            <tr v-for="(value, field) in item" :key="value" v-if="!field.startsWith('_')">
              <td width="20%">
                {{ field }}:
              </td>
              <td>
                {{ value }}
              </td>
            </tr>
            </tbody>
          </table>
          </div>
          <div width="50%" style="padding-left: 20px" v-if="showJobHeading(leg, index) && print_options.enable_stop_actions === '1' && print_options.enable_start_at === '1' && leg.start_stop.type === 'pickup'">
            <div v-for="(actions_j, key_j) in leg.start_stop_actions" :key="key_j">
              <span class="has-text-grey is-size-6" v-show="key_j === 0 && actions_j.length !== 0">
                <div v-if="(print_options.enable_items === '0' || !leg.end_job.items) && print_options.enable_map_each_job === '0'">
                  <br />
                  <br />
                  <br />
                  <br />
                </div>
                Stop Arrived Actions
              </span>
              <span class="has-text-grey is-size-6" v-show="key_j === 1 && actions_j.length !== 0">
                <div v-if="(print_options.enable_items === '0' || !leg.end_job.items) && print_options.enable_map_each_job === '0'">
                  <br />
                  <br />
                  <br />
                  <br />
                </div>
                Stop Completed Actions
              </span>
              <table class="stop_action_table" style="width: 100%; margin-bottom: 20px;" align="right">
                  <tbody v-for="action_j of actions_j" :key="action_j">
                    <tr>
                      <td>
                        <p><b>{{action_j.button_text}}</b></p>
                        <p>{{action_j.description}}</p>
                        <p v-if="action_j.date_completed"><small>{{action_j.date_completed | formatDate}}</small></p>
                        <br />
                      </td>
                      <td v-if='action_j.type.id === "take_video"' style="width: 300px">
                        <p><b>Video: </b></p>
                        <div v-if='action_j.attachments'>
                          <p style="word-break:break-all"><a :href="action_j.attachments[0].url">{{action_j.attachments[0].url}}</a></p>
                          <br/>
                        </div>
                        <div v-else>
                          Pending
                        </div>
                      </td>
                      <td v-if='action_j.type.id === "take_picture" '>
                        <p><b>Picture: </b></p>
                        <div v-if='action_j.attachments'>
                        <p><img :src= "action_j.attachments[0].url" ><img></p>
                        </div>
                        <div v-else>
                          Pending
                        </div>
                      </td>
                      <td v-if='action_j.type.id === "order_acknowledgement"'>
                        <p><b>Signature: </b></p>
                        <div v-if='action_j.attachments'>
                        <img :src= "action_j.attachments[0].url"><img>
                        </div>
                        <div v-else>
                          Pending
                        </div>
                        <div v-if='action_j.type_data'>
                          <div v-for="items_j of action_j.type_data.items" :key="items_j" v-if='action_j.type_data'>
                            <tr v-for="(value_j, field_j) in items_j" :key="value_j" >
                            <div v-if="!field_j.startsWith('_')">
                              <td width="20%">
                                {{ field_j }}:&nbsp;
                              </td>
                              <td>
                                {{ value_j }}
                              </td>
                            </div>
                            <div v-if="field_j === '_status'" >
                              <td width="20%">
                                Status:&nbsp;
                              </td>
                              <td width="20%">
                                {{ value_j.status.display}}
                              </td>
                            </div>
                            </tr>
                            <br/>
                          </div>
                        </div>
                      </td>
                      <td v-if='action_j.type.id === "confirmation"'>
                        <div v-if= 'action_j.type_data'>
                          <p><b>Note :</b> {{action_j.type_data.notes}}</p>
                        </div>
                        <p><b>Signature: </b></p>
                        <div v-if="action_j.attachments">
                        <img :src= "action_j.attachments[0].url"><img>
                        </div>
                        <div v-else>
                          Pending
                        </div>
                      </td>
                      <td v-if='action_j.type.id === "get_signature"'>
                        <p><b>Signature: </b></p>
                        <div v-if="action_j.attachments">
                        <img :src= "action_j.attachments[0].url"><img>
                        </div>
                        <div v-else>
                          Pending
                        </div>
                      </td>
                    </tr>
                  </tbody>
              </table>
            </div>
          </div>
        </td>
      </tr>
      <tr>
        <td width="50%">
          <table>
              <tr>
                <td>
                  <span class="has-text-grey is-size-6">Arrive at:</span>
                  <br />{{ leg.end_stop.location.long_name }}
                  <span v-if="print_options.enable_dates == 1">
                      <br />
                      <span v-html="getStopCompletedDate(leg.end_stop)"></span>
                    </span>
                  <br />
                  <br />
                  <span v-if="leg.end_stop.driver_notes"><strong>Driver note:</strong> {{leg.end_stop.driver_notes}}</span>
                  <span class="has-text-grey is-size-6"></span>
                </td>
              </tr>
          </table>
        </td>
        <td width="50%" style="padding-left: 20px" v-if="showJobHeading(leg, index) && print_options.enable_stop_actions === '1'">
            <div v-for="(actions_i, key_i) in leg.end_stop_actions" :key="key_i">
            <span class="has-text-grey is-size-6" v-if="key_i === 0 && actions_i.length !== 0">Stop Arrived Actions</span>
            <span class="has-text-grey is-size-6" v-if="key_i === 1 && actions_i.length !== 0">Stop Completed Actions</span>
            <table class="stop_action_table" style="width: 100%;" align="right">
                <tbody v-for="action_i of actions_i" :key="action_i">
                  <tr>
                    <td>
                      <p><b>{{action_i.button_text}}</b></p>
                      <p>{{action_i.description}}</p>
                      <p v-if="action_i.date_completed"><small>{{action_i.date_completed | formatDate}}</small></p>
                      <br />
                    </td>
                    <td v-if='action_i.type.id === "take_video"' style="width: 300px">
                      <p><b>Video: </b></p>
                      <div v-if='action_i.attachments'>
                        <p style="word-break:break-all"><a :href="action_i.attachments[0].url">{{action_i.attachments[0].url}}</a></p>
                        <br/>
                      </div>
                      <div v-else>
                        Pending
                      </div>
                    </td>
                    <td v-if='action_i.type.id === "take_picture"'>
                      <b><p>Picture: </p></b>
                      <div v-if='action_i.attachments'>
                      <p><img :src= "action_i.attachments[0].url" ><img></p>
                      </div>
                      <div v-else>
                        Pending
                      </div>
                    </td>
                    <td v-if='action_i.type.id === "order_acknowledgement"'>
                      <p><b>Signature: </b></p>
                      <div v-if='action_i.attachments'>
                      <img :src= "action_i.attachments[0].url"><img>
                      </div>
                      <div v-else>
                        Pending
                      </div>
                      <div v-if='action_i.type_data'>
                        <div v-for=" items_i of action_i.type_data.items" :key="items_i" >
                          <tr v-for="(value_i, field_i) in items_i" :key="value_i">
                            <div v-if="!field_i.startsWith('_')">
                              <td width="20%">
                                {{ field_i }}:&nbsp;
                              </td>
                              <td>
                                {{ value_i }}
                              </td>
                            </div>
                            <div v-if="field_i === '_status'" >
                              <td width="20%">
                                Status:&nbsp;
                              </td>
                              <td width="20%">
                                {{ value_i.status.display}}
                              </td>
                            </div>
                          </tr>
                          <br/>
                        </div>
                      </div>
                    </td>
                    <td v-if='action_i.type.id === "confirmation"'>
                      <div v-if= 'action_i.type_data'>
                        <p><b>Note :</b> {{action_i.type_data.notes}}</p>
                      </div>
                      <p><b>Signature: </b></p>
                      <div v-if="action_i.attachments">
                      <img :src= "action_i.attachments[0].url"><img>
                      </div>
                      <div v-else>
                        Pending
                      </div>
                    </td>
                    <td v-if='action_i.type.id === "get_signature"'>
                      <p><b>Signature: </b></p>
                      <div v-if="action_i.attachments">
                        <img :src= "action_i.attachments[0].url"><img>
                      </div>
                      <div v-else>
                        Pending
                      </div>
                    </td>
                  </tr>
                </tbody>
            </table>
          </div>
        </td>
      </tr>
      <tr>
        <td colspan="2"><hr /></td>
      </tr>
      </tbody>
    </table>
    <!-- End Job Table -->
    <div class="columns is-centered no-print">
      <div class="column is-full has-text-centered">
        <button class="button is-primary" @click="print()"><i class="fa fa-print"></i>&nbsp;Print</button>
      </div>
    </div>
  </section>
</template>

<script>
  import { BREAK_ICON_CONFIG } from "@/common/constants/mapSettings";
  import qs from 'qs';
  import BatchService from './service'
  import ManifestHeader from './ManifestHeader'
  import JobService from '@/components/job/service'
  import CompanyService from '@/components/company/service'
  import DriverService from '@/components/driver/service'
  import LocationService from '@/components/location/service'
  import LocalStorageService from '@/common/local_storage'
  import StatusService from '@/components/job/status_service'
  import ManifestMap from './ManifestMap.vue';
  import { gmapApi } from 'gmap-vue';

  export default {

    name: 'PrintBatchDetails',

    components: { ManifestHeader, ManifestMap },
    computed: {
    google: gmapApi,
    shouldShowActualTravelCheckbox(){
      return this.print_options.enable_directions == 1 && this.has_actual_travelled_steps
    }
  },
  data() {
    return {
      error_message: null,
      batch_id: null,
      job_id: null,
      breaks: [],
      encoded_data: null,
      batched_jobs: [],
      route_data: {},
      legs: [],
      static_map_url: null,
      company: null,
      driver: null,
      vehicle: null,
      print_options: {
        enable_directions: 1,
        enable_dates: 1,
        enable_items: 1,
        enable_page_breaks: 1,
        enable_page_header: 1,
        enable_actual_directions: 0,
        enable_breaks: 1,
      },
      markers: null,
      path_actual_travelled: null,
      path_suggested_travelled: null,
      markersJobPair: null,
      is_batch: null,
      has_actual_travelled_steps: null,
    }
  },

    beforeMount(){
      let is_batch = this.is_batch = false;
      let batch_id = this.batch_id = this.$route.query.batch_id || null;
      let job_id = this.job_id = this.$route.query.job_id || null;
      let encoded_data = this.encoded_data = this.$route.query.enc || null;
      let self = this;
      let markers = this.markers = [];
      let path_actual_travelled = this.path_actual_travelled = [];
      let path_suggested_travelled = this.path_suggested_travelled = [];
      let markersJobPair = this.markersJobPair = {};
      if (!this.batch_id && !this.job_id && !this.encoded_data){
        this.error_message = 'No batch_id or job_id supplied'
      }

      if (batch_id){
        _initBatch(batch_id);
      }else if (job_id){
        _initSingleJob(job_id);
      }else if (encoded_data){
        _initEncodedData(encoded_data);
      }

      if (batch_id || job_id){
        CompanyService.getSessionCompany((err, company) => {

          if (err){
            alert(err);
            return;
          }

          this.company = company;
        })
      }

    function _initSingleJob(job_id){

      // Load batched jobs
      JobService.getJob(job_id, (err, job) => {

        if (err) {
          console.log("BatchService.getJobs error:", err);
          return;
        }

        if (!job){
          alert('No job found for id ' + job_id);
          return;
        }

        let jobs = [job]
        self.batched_jobs = jobs;
        self.buildBreakList(job);
        // Load the driver & vehicle
        DriverService.get(jobs[0].driver_id, (err, driver) => {

          if (!err && driver){
            self.driver = driver;
            self.vehicle = jobs[0].vehicle || driver.assigned_to_vehicle;

          }

          // Get the route
          JobService.getRoute(job_id, (err, route) => {
            if (err) {
              console.log("getBatchRoute error:", err);
              return;
            }

            self.route_data = route;
            self.populateMap()
            
          })

        })

      })

    }

    function _initBatch(batch_id){
      self.is_batch = true;
      // Load batched jobs
      BatchService.getJobs(batch_id, (err, jobs) => {

        if (err) {
          console.log("BatchService.getJobs error:", err);
          return;
        }

        self.batched_jobs = jobs;

        // Load the driver & vehicle
        self.buildBreakList(jobs);
        DriverService.get(jobs[0].driver_id, (err, driver) => {

          if (err) {
            console.log("DriverService.get error:", err);
            return;
          }

          self.driver = driver;
          self.vehicle = jobs[0].vehicle || driver.assigned_to_vehicle;

          // Get the route
          BatchService.getBatchRoute(batch_id, (err, route) => {

            if (err) {
              console.log("getBatchRoute error:", err);
              return;
            }

            self.route_data = route;
            self.populateMap()
          })

        })

      })

    }

    function _initEncodedData(encoded_data){

      let buff = new Buffer(encoded_data, 'base64');
      let decoded_data = JSON.parse(buff.toString('utf8'));

      if(decoded_data.print_options){
        self.print_options = decoded_data.print_options;
        self.updatePrintOptions()
      }

      let job = decoded_data.job;
      let jobs = null;
      if (job){
        jobs = [job]
      }else if (decoded_data.jobs){
        jobs = decoded_data.jobs
      }

      if (!jobs){
        alert('No job found for id ' + job_id);
        return;
      }

      self.batched_jobs = jobs;
      self.driver = jobs[0].driver;
      self.vehicle = jobs[0].vehicle || jobs[0].driver.assigned_to_vehicle;
      self.route_data = decoded_data.route;
      self.company = decoded_data.company;
      self.static_map_url = decoded_data.static_map? decoded_data.static_map.url : null;

      self.populateMap()

    }

  },

  mounted() {
    this.initPrintOptions()
  },

  filters: {
    capitalize: function (value) {
      if (!value) return ''
      value = value.toString()
      return value.charAt(0).toUpperCase() + value.slice(1)
    }
  },

  methods: {

    getVehicleDisplayName(){

      if (!this.vehicle){
        return;
      }

      if (this.vehicle.name){
        return this.vehicle.name
      }else{
        return `${this.vehicle.year} ${this.vehicle.color} ${this.vehicle.make} ${this.vehicle.model}`
      }

    },

    getTotalDuration(){

      if (!this.route_data || !this.route_data.route_summary || !this.route_data.route_summary.total_duration_seconds){
        return 0;
      }

      let total = parseInt(this.route_data.route_summary.total_duration_seconds)
      total = parseFloat((total / 60).toFixed(2))
      return total

    },

    getTotalDistance(){

      if (!this.route_data || !this.route_data.route_summary || !this.route_data.route_summary.total_distance_meters){
        return 0;
      }

      let total = parseInt(this.route_data.route_summary.total_distance_meters)
      total = parseFloat((total * 0.00062137).toFixed(2))
      return total

    },

    showJobHeading(leg, index){

      if (index === 0){
        return true;
      }

      let prev_leg = this.legs[index-1];
      if (prev_leg && parseInt(prev_leg.end_job.job_id) !== parseInt(leg.end_job.job_id)){
        return true;
      }

      return false;

    },

    print(){
      window.print();
    },

    getBatchStartDate(){

      if (!this.batched_jobs || !this.batched_jobs.length){
        return '';
      }

      return this.batched_jobs[0].start_date || this.batched_jobs[0].pickup_date;

    },

    updatePrintOptions(option){
      if(option === 'breaks'){       
        this.populateJobMarkers()
        this.populateMarkers()
      }
      return LocalStorageService.set('print_options', JSON.stringify(this.print_options));
    },

    initPrintOptions(){
      let options = LocalStorageService.get('print_options');
      if (options && options.length){
        try {
          this.print_options = JSON.parse(options);
        } catch(e) {
          this.updatePrintOptions();
        }
      }else{
        // Write default print options to localstorage
        this.updatePrintOptions();
      }
    },

    buildBreakList(jobs){

      if(this.is_batch){
        jobs.forEach( job => {
          JobService.getBreaksByDriver({job_id : job.job_id}, (err, breaks) => {
            if (err) {
              console.log("getBreaks error: ", err)
              return;
            }
            this.breaks[job.job_id] = breaks;
            })
          })
      }
      else{
        JobService.getBreaksByDriver({job_id : jobs.job_id}, (err, breaks) => {
          if (err) {
            console.log("getBreaks error: ", err)
            return;
          }

          this.breaks[jobs.job_id] = breaks;
        })
      }
    },

    getStopStartDate(stop){

      let str = 'Arrive at ';
      let date = stop.arrival_date;
      if (StatusService.isCompleted(stop)){
        str = 'Completed at ';
        date = stop.completed_date;
      }else if (StatusService.isInProgressAndNotCompleted(stop)){
        str = 'Started at ';
        date = stop.in_progress_date;
      }

      return str + this.$options.filters.formatDate(date);
    },

    getStopCompletedDate(stop){

      let str = null;
      let date = null;
      if (StatusService.isCompleted(stop)){
        str = 'Completed at ';
        date = stop.completed_date;
      }else if (StatusService.isInProgressAndNotCompleted(stop)){
        str = 'Started at ';
        date = stop.in_progress_date
      }

      if (date){
        str = str + this.$options.filters.formatDate(date);
      }

      return str;

    },

    getSteps(leg){

      if(!leg){
        return null
      }

      if(this.print_options.enable_actual_directions == 1){
        return leg.actual_traveled_steps
      } else {
        return leg.steps
      }
    },

    shouldIncludePageBreaks(){
      return this.print_options.enable_page_breaks == 1
    },

    shouldShowPageHeader(){
      return this.print_options.enable_page_header == 1
    },

    getJobContactName(job_id, job_stop_id){

      if (!this.batched_jobs || !this.batched_jobs.length){
        return '';
      }

      let found_job = {};
      let found_stop = {};
      job_id = parseInt(job_id)
      job_stop_id = parseInt(job_stop_id)
      for (let job of this.batched_jobs){
        if (parseInt(job.job_id) === job_id){
          found_job = job;
          for (let stop in job.stops){
            if (parseInt(stop.job_stop_id) === job_stop_id){
              found_stop = stop;
              break;
            }
          }
          break;
        }
      }

      return found_stop.contact_name || found_job.contact_name || '';

    },

    populateMap(){
      this.populatePolyLines()
      this.populateLegs()
      this.populateMarkers()
      this.populateJobMarkers()
    },

    populateJobMarkers(){

      if(!this.batched_jobs){
        return
      }

      for (let job of this.batched_jobs){
        let temp = [];
        let index = 0;
          this.print_options.enable_breaks === '1' ? 
            this.breaks[job.job_id].forEach( element => {
                temp.push({
                  id: 0,
                  position: { lat: element.in_progress_location.latitude, lng: element.in_progress_location.longitude},
                  icon: BREAK_ICON_CONFIG,
                  zIndex: index,
                })
                index ++;
            }) : null;

        for (let i = 0; i< job.stops.length; i++){
          let index = i + 1;
          temp.push(
            {
              id: 0,
              position: { lat: job.stops[i].location.latitude, lng: job.stops[i].location.longitude },
              label: {text: index.toString()},
              zIndex: index,
            }
          )
          index ++;

        }
        this.markersJobPair[job.job_id] = temp
      }

    },
    populatePolyLines(){

      if(!this.route_data){
        return;
      }

      if(this.route_data.actual_traveled_path_polyline && this.path_actual_travelled !== null){
        this.path_actual_travelled.push(this.route_data.actual_traveled_path_polyline);
      }

      if(this.route_data.suggested_path_polyline){
        this.path_suggested_travelled.push(this.route_data.suggested_path_polyline);
      }
    },

    populateMarkers(){
      let index = 0;
      const temp = [];
      if(!this.route_data || !this.route_data.markers){
        return;
      }

      this.print_options.enable_breaks === '1'? this.breaks.forEach( element => {
        element.forEach( sub_element => {
          temp.push({
            id: 0,
            position: { lat: sub_element.in_progress_location.latitude, lng: sub_element.in_progress_location.longitude},
            icon: BREAK_ICON_CONFIG,
            zIndex: index,
          })
          index ++;
        })
      }) : null;

      for (let i in this.route_data.markers) {
        let marker = this.route_data.markers[i];
        temp.push({
          id: 0,
          position: { lat: marker.location.latitude, lng: marker.location.longitude },
          label: {text: marker.label.toString()},
          zIndex: index,
        })
        index ++;
      }

      this.markers = temp;

    },

    populateLegs(){

      if(!this.route_data || !this.route_data.directions || !this.route_data.directions.routes || !this.route_data.directions.routes[0] || !this.route_data.directions.routes [0].legs){
        return
      }

      // Append full job and stop objects to each leg
      // We append the original stop object because it's order is different from whats returned form the route endpoint
      // This is so we can determine when to show job details vs just stop details
      let legs = this.route_data.directions.routes[0].legs;
      for (let leg of legs) {

        if(leg.actual_traveled_steps && leg.actual_traveled_steps.length){
          this.has_actual_travelled_steps = true;
        }

        let start_job_id = parseInt(leg.start_stop.job_id);
        let end_job_id = parseInt(leg.end_stop.job_id);

        for (let job of this.batched_jobs) {
          if (parseInt(job.job_id) === start_job_id) {
            leg.start_job = job;
          }
          if (parseInt(job.job_id) === end_job_id) {
            leg.end_job = job;
          }
        }
        leg = addStopActions(leg);

      }

      this.legs = legs;

      function addStopActions(leg){
        let start_completed_actions = [];
        let start_arrived_actions = [];
        let end_completed_actions = [];
        let end_arrived_actions = [];
        let start_stop_actions = [];
        let end_stop_actions = [];
        let start_actions = leg.start_stop.job_stop_actions;
        let stop_actions = leg.end_stop.job_stop_actions;
        if(start_actions){
          for (let action of start_actions) {
            if (action.stop_status_id === 4){
              start_arrived_actions.push(action);
            }
            else{
              start_completed_actions.push(action);
            }
          }
        }

        if(stop_actions){
          for (let action of stop_actions){
            if (action.stop_status_id === 4){
              end_arrived_actions.push(action);
            }
            else{
              end_completed_actions.push(action);
            }
          }
        }

        start_stop_actions.push(start_arrived_actions, start_completed_actions);
        end_stop_actions.push(end_arrived_actions, end_completed_actions);
        leg["start_stop_actions"] = start_stop_actions;
        leg["end_stop_actions"] = end_stop_actions;
        return leg;
      }
    }
  }
}

</script>
<style>
@media print {
  body {
    -webkit-print-color-adjust: exact;
  }
  .no-print {
    display: none !important;
    visibility: hidden !important;
  }
  .page-break {
    page-break-before: always;
  }
}
.batch-details {
  max-width: 1000px;
  margin: 0 auto;
}
.batch-details .table td{
  padding: 0;
}

.batch-details .table ol{
  padding-left: 15px;
}
.batch-details .table ol li{
  margin-bottom: 10px;
}
.batch-details hr {
  margin: 2rem 0;
}

.map {
  width: 330px;
}

.substep .stepicon{
  overflow:hidden;
  position:relative;
  top:0;
  left:0;
  width:16px;
  height:16px
}
.maneuver{
  background-image: url(./static/maneuvers.png);
  background-size:19px 630px;
  width:16px;
  height:16px
}
.maneuver.ferry{
  background-position:0 -614px
}
.maneuver.ferry-train{
  background-position:0 -566px
}
.maneuver.merge{
  background-position:0 -143px
}
.maneuver.straight{
  background-position:0 -534px
}
.maneuver.fork-left{
  background-position:0 -550px
}
.maneuver.ramp-left{
  background-position:0 -598px
}
.maneuver.roundabout-left{
  background-position:0 -197px
}
.maneuver.turn-left,.maneuver.keep-left{
  background-position:0 -413px
}
.maneuver.turn-sharp-left{
  background-position:0 0
}
.maneuver.turn-slight-left{
  background-position:0 -378px
}
.maneuver.uturn-left{
  background-position:0 -305px
}
.maneuver.fork-right{
  background-position:0 -499px
}
.maneuver.ramp-right{
  background-position:0 -429px
}
.maneuver.roundabout-right{
  background-position:0 -232px
}
.maneuver.turn-right,.maneuver.keep-right{
  background-position:0 -483px
}
.maneuver.turn-sharp-right{
  background-position:0 -582px
}
.maneuver.turn-slight-right{
  background-position:0 -51px
}
.maneuver.uturn-right{
  background-position:0 -35px
}
.tags .tag{
  height: 1.5em;
  background-color: white !important;
}
.tags .route{
  padding-bottom:13px;
  margin-bottom:13px;
  font-size: 30px;
  font-style: italic;
}
.stop_action_table td {
  width: 50%;
}
</style>



// WEBPACK FOOTER //
// src/components/job/batch/PrintBatchDetails.vue